import React from 'react';
import { Icon, FormFieldset } from '@tesla/design-system-react';
import { iconPlusSmall } from '@tesla/design-system-icons';
import { Checkbox } from '@tesla/informed-tds';
import { ArrayField, FormField, Relevant } from 'informed';
import { arrayOf, func, number, oneOfType, shape, string } from 'prop-types';
import { useIntl } from 'react-intl';
import messages from './messages';
import style from './style.module.scss';

const Employers = ({
  jobNoPriorWorkExperience,
  initialValue,
  label,
  types,
  max,
  validate,
  employers,
  ...props
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;

  const schema = {
    type: 'object',
    properties: [
      {
        companyName: {
          type: 'string',
          title: formatMessage(messages.CompanyName),
          'ui:control': 'input',
          'ui:props': {
            required: true,
          },
        },
      },
      {
        jobTitle: {
          type: 'string',
          title: formatMessage(messages.JobTitle),
          'ui:control': 'input',
          'ui:props': {
            required: true,
          },
        },
      },
      {
        startDate: {
          type: 'string',
          format: 'date-time',
          'ui:control': 'date',
          'ui:autocomplete': 'off',
          title: formatMessage(messages.StartDate),
          'ui:props': {
            required: true,
            keepStateIfRelevant: true,
            initialValue: new Date(),
          },
        },
      },
      {
        isCurrent: {
          type: 'boolean',
          title: formatMessage(messages.IsCurrent),
          'ui:control': 'checkbox',
          'ui:props': {
            keepStateIfRelevant: true,
          },
        },
      },
      {
        endDate: {
          type: 'string',
          format: 'date-time',
          'ui:control': 'date',
          'ui:autocomplete': 'off',
          title: formatMessage(messages.EndDate),
          'ui:props': {
            relevant: ({ scope, formApi }) => {
              const isCurrent = formApi.getValue(`${scope}.isCurrent`);
              return !isCurrent;
            },
            keepStateIfRelevant: true,
            required: true,
          },
        },
      },
      {
        summaryDuties: {
          type: 'string',
          title: formatMessage(messages.SummaryDuties),
          'ui:control': 'textarea',
          'ui:props': {
            required: true,
          },
        },
      },
    ],
  };

  const validateEndDate = (value, values, { scope, formApi }) => {
    if (
      formApi.getValue(scope).endDate &&
      formApi.getValue(scope).endDate <= formApi.getValue(scope).startDate
    ) {
      return messages.EndDate.errorMessage;
    }

    return undefined;
  };

  const validateStartDate = (value, values, { scope, formApi }) => {
    if (
      formApi.getValue(scope).endDate &&
      formApi.getValue(scope).endDate <= formApi.getValue(scope).startDate
    ) {
      return messages.StartDate.errorMessage;
    }

    return undefined;
  };

  return (
    <>
      <div className={`${style.Header}`}>
        <h4 className={`${style.Title} ${style.ExperienceTitleOverride}`}>
          {formatMessage(messages.Title)}
        </h4>
        <Checkbox
          name="jobNoPriorWorkExperience"
          label={formatMessage(messages.JobNoPriorWorkExperience)}
          keepState
        />
      </div>
      <Relevant
        when={({ formApi }) => {
          const hasNotExperience = formApi.getValue(`workExperience.jobNoPriorWorkExperience`);
          return !hasNotExperience;
        }}
      >
        <FormFieldset className={`${style.Employers}`}>
          <ArrayField {...props} initialValue={initialValue}>
            {({ add, arrayFieldState: { fields: items } }) => (
              <>
                <ArrayField.Items>
                  {({ index, remove }) => (
                    <div className={`${style.Employer}`}>
                      {max > 1 && (
                        <>
                          <div className={`${style.CaptionWrapper}`}>
                            <span className={`tds-text--h5 ${style.Caption}`}>
                              {formatMessage(messages.Caption, {
                                index: index + 1,
                              })}
                            </span>
                          </div>
                          <div className={`${style.CaptionRemoveWrapper}`}>
                            {index > 0 && (
                              <button
                                className={`tds-link ${style.Remove}`}
                                type="button"
                                onClick={remove}
                              >
                                {formatMessage(messages.RemoveButton)}
                              </button>
                            )}
                          </div>
                        </>
                      )}
                      {schema.properties.map((field) => (
                        <div className={`${style.LinkWrapper}`}>
                          {Object.prototype.hasOwnProperty.call(field, 'endDate') && (
                            <FormField
                              name={Object.keys(field)[0]}
                              schema={{ properties: field }}
                              validate={validateEndDate}
                            />
                          )}
                          {Object.prototype.hasOwnProperty.call(field, 'startDate') && (
                            <FormField
                              name={Object.keys(field)[0]}
                              schema={{ properties: field }}
                              validate={validateStartDate}
                            />
                          )}
                          {!(
                            Object.prototype.hasOwnProperty.call(field, 'startDate') ||
                            Object.prototype.hasOwnProperty.call(field, 'endDate')
                          ) && (
                            <FormField
                              name={Object.keys(field)[0]}
                              schema={{ properties: field }}
                              validate={validate}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </ArrayField.Items>
                {max > 1 && (
                  <button
                    className={`tds-icon-trigger tds-icon-trigger--small ${style.Add}`}
                    type="button"
                    onClick={add}
                    disabled={items.length >= max}
                  >
                    <Icon
                      data={iconPlusSmall}
                      className={`${style.Icon} ${style.IconWithCircle}`}
                    />
                    <span className={`${style.AddLabel}`}>
                      {items.length > 0
                        ? formatMessage(messages.AddMoreButton)
                        : formatMessage(messages.AddButton)}
                    </span>
                  </button>
                )}
              </>
            )}
          </ArrayField>
        </FormFieldset>
      </Relevant>
    </>
  );
};

Employers.propTypes = {
  jobNoPriorWorkExperience: Boolean,
  initialValue: arrayOf(
    shape({
      type: string,
      link: string,
    })
  ),
  label: string,
  types: arrayOf(
    shape({
      const: string,
      title: string,
    })
  ).isRequired,
  max: oneOfType([number, string]),
  validate: func,
  name: string.isRequired,
  employers: shape({}),
};

Employers.defaultProps = {
  jobNoPriorWorkExperience: false,
  initialValue: [],
  label: '',
  max: 4,
  validate: null,
  employers: {},
};

export default Employers;
